/**
 * Click Behavior Detection Strategy
 * Monitors specific elements for click positioning patterns
 *
 * Enhanced detection:
 * - Click-mouse position delta (bots using .click() have mismatched positions)
 * - Pre-click mouse trajectory analysis
 * - Time between last mousemove and click
 */
import { BaseStrategy } from '../strategy.js';
export class ClickStrategy extends BaseStrategy {
    constructor(options) {
        super();
        this.name = 'click';
        this.defaultWeight = 0.30;
        this.events = [];
        this.targetSelectors = ['button', 'a', 'input[type="submit"]', '[role="button"]'];
        this.lastMousePosition = null;
        this.mouseListener = null;
        this.clickListeners = new Map();
        this.isActive = false;
        if (options === null || options === void 0 ? void 0 : options.targetSelectors) {
            this.targetSelectors = options.targetSelectors;
        }
    }
    start() {
        if (this.isActive)
            return;
        this.isActive = true;
        // Track mouse position globally with timestamp
        this.mouseListener = (e) => {
            const mouseEvent = e;
            this.lastMousePosition = {
                x: mouseEvent.clientX,
                y: mouseEvent.clientY,
                timestamp: Date.now(),
            };
        };
        document.addEventListener('mousemove', this.mouseListener, { passive: true });
        // Find all matching elements and attach listeners
        this.attachClickListeners();
    }
    /**
     * Add a new target selector at runtime
     */
    addTarget(selector) {
        if (!this.targetSelectors.includes(selector)) {
            this.targetSelectors.push(selector);
            // Attach listeners if already active
            if (this.isActive) {
                this.attachClickListenersForSelector(selector);
            }
        }
    }
    attachClickListeners() {
        this.targetSelectors.forEach(selector => {
            this.attachClickListenersForSelector(selector);
        });
    }
    attachClickListenersForSelector(selector) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
            if (this.clickListeners.has(element))
                return; // Already attached
            const listener = (e) => {
                var _a, _b;
                const clickEvent = e;
                const now = Date.now();
                const rect = element.getBoundingClientRect();
                // Check if element is in viewport
                const inViewport = (rect.top >= 0 &&
                    rect.left >= 0 &&
                    rect.bottom <= window.innerHeight &&
                    rect.right <= window.innerWidth);
                // Calculate mouse-click position delta
                // Bots using element.click() have click event coordinates different from mouse position
                let mouseClickDelta = 0;
                let timeSinceMouseMove = -1;
                if (this.lastMousePosition) {
                    const dx = clickEvent.clientX - this.lastMousePosition.x;
                    const dy = clickEvent.clientY - this.lastMousePosition.y;
                    mouseClickDelta = Math.sqrt(dx * dx + dy * dy);
                    timeSinceMouseMove = now - this.lastMousePosition.timestamp;
                }
                // Analyze click position
                let position;
                if (!this.lastMousePosition) {
                    position = 'no-mouse-data'; // Bot - no mouse movement
                }
                else {
                    const mx = this.lastMousePosition.x;
                    const my = this.lastMousePosition.y;
                    // Check if mouse was over element
                    const overElement = (mx >= rect.left &&
                        mx <= rect.right &&
                        my >= rect.top &&
                        my <= rect.bottom);
                    if (!overElement) {
                        position = 'outside'; // Bot - mouse not over target
                    }
                    else {
                        // Check if dead center (suspicious)
                        const centerX = rect.left + rect.width / 2;
                        const centerY = rect.top + rect.height / 2;
                        const distanceFromCenter = Math.sqrt((mx - centerX) ** 2 + (my - centerY) ** 2);
                        // Dead center = within 2px of center
                        if (distanceFromCenter < 2) {
                            position = 'dead-center'; // Suspicious - too perfect
                        }
                        else {
                            position = 'over-element'; // Human - somewhere on the button
                        }
                    }
                }
                this.events.push({
                    clickX: clickEvent.clientX,
                    clickY: clickEvent.clientY,
                    mouseX: (_a = this.lastMousePosition) === null || _a === void 0 ? void 0 : _a.x,
                    mouseY: (_b = this.lastMousePosition) === null || _b === void 0 ? void 0 : _b.y,
                    rect,
                    inViewport,
                    position,
                    timestamp: now,
                    mouseClickDelta,
                    timeSinceMouseMove,
                    isTrusted: clickEvent.isTrusted,
                });
                // Notify detector - clicks are high-value events
                this.notifyEvent(1.0);
            };
            element.addEventListener('click', listener);
            this.clickListeners.set(element, listener);
        });
    }
    stop() {
        if (!this.isActive)
            return;
        this.isActive = false;
        // Remove mouse listener
        if (this.mouseListener) {
            document.removeEventListener('mousemove', this.mouseListener);
            this.mouseListener = null;
        }
        // Remove all click listeners
        this.clickListeners.forEach((listener, element) => {
            element.removeEventListener('click', listener);
        });
        this.clickListeners.clear();
    }
    reset() {
        this.events = [];
        this.lastMousePosition = null;
    }
    score() {
        const eventCount = this.events.length;
        if (eventCount === 0)
            return undefined;
        // Pre-compute counts in a single pass
        let positionScore = 0;
        let clicksWithDelta = 0;
        let mismatchCount = 0;
        let clicksWithTiming = 0;
        let suspiciousTimingCount = 0;
        let untrustedCount = 0;
        for (const click of this.events) {
            // Position scoring
            switch (click.position) {
                case 'no-mouse-data':
                    positionScore += 0.0;
                    break;
                case 'outside':
                    positionScore += 0.0;
                    break;
                case 'dead-center':
                    positionScore += 0.5;
                    break;
                case 'over-element':
                    positionScore += 1.0;
                    break;
            }
            // Delta scoring
            if (click.mouseClickDelta >= 0) {
                clicksWithDelta++;
                if (click.mouseClickDelta > 5)
                    mismatchCount++;
            }
            // Timing scoring
            if (click.timeSinceMouseMove >= 0) {
                clicksWithTiming++;
                if (click.timeSinceMouseMove < 10 || click.timeSinceMouseMove > 2000) {
                    suspiciousTimingCount++;
                }
            }
            // Trusted scoring
            if (!click.isTrusted)
                untrustedCount++;
        }
        let score = 0;
        let factors = 0;
        // 1. Position-based scoring
        score += positionScore / eventCount;
        factors++;
        // 2. Mouse-click delta scoring
        if (clicksWithDelta >= 2) {
            const mismatchRatio = mismatchCount / clicksWithDelta;
            let deltaScore;
            if (mismatchRatio >= 0.8)
                deltaScore = 0.1;
            else if (mismatchRatio >= 0.5)
                deltaScore = 0.3;
            else if (mismatchRatio >= 0.3)
                deltaScore = 0.5;
            else if (mismatchRatio > 0)
                deltaScore = 0.7;
            else
                deltaScore = 1.0;
            score += deltaScore;
            factors++;
        }
        // 3. Mouse-to-click timing
        if (clicksWithTiming >= 2) {
            const suspiciousRatio = suspiciousTimingCount / clicksWithTiming;
            let timingScore;
            if (suspiciousRatio >= 0.8)
                timingScore = 0.2;
            else if (suspiciousRatio >= 0.5)
                timingScore = 0.4;
            else if (suspiciousRatio >= 0.3)
                timingScore = 0.6;
            else if (suspiciousRatio > 0)
                timingScore = 0.8;
            else
                timingScore = 1.0;
            score += timingScore;
            factors++;
        }
        // 4. Trusted event scoring
        if (eventCount >= 2) {
            const untrustedRatio = untrustedCount / eventCount;
            let trustedScore;
            if (untrustedRatio >= 0.5)
                trustedScore = 0.1;
            else if (untrustedRatio > 0)
                trustedScore = 0.3;
            else
                trustedScore = 1.0;
            score += trustedScore;
            factors++;
        }
        return factors > 0 ? score / factors : undefined;
    }
    getDebugInfo() {
        return {
            eventCount: this.events.length,
            positions: {
                noMouseData: this.events.filter(e => e.position === 'no-mouse-data').length,
                outside: this.events.filter(e => e.position === 'outside').length,
                deadCenter: this.events.filter(e => e.position === 'dead-center').length,
                overElement: this.events.filter(e => e.position === 'over-element').length,
            },
            inViewport: this.events.filter(e => e.inViewport).length,
            trackedElements: this.clickListeners.size,
            mouseClickDeltas: this.events.map(e => e.mouseClickDelta),
            timeSinceMouseMoves: this.events.map(e => e.timeSinceMouseMove),
            untrustedClicks: this.events.filter(e => !e.isTrusted).length,
        };
    }
}
